import { createContext, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import type { Session, SupabaseClient } from "@supabase/supabase-js";

import { signInWithSupabase, signOut, type Credentials, type SignInResult } from "./auth";

export interface AuthUser {
  id: string;
  email: string | null;
  user_metadata: { readonly full_name?: unknown } | null;
}

export type AuthStatus = "loading" | "authenticated" | "unauthenticated";

export interface AuthContextValue {
  status: AuthStatus;
  user: AuthUser | null;
  signIn: (credentials: Credentials) => Promise<SignInResult>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * Wraps the app in supabase-js-backed auth. On mount it restores any persisted session
 * (stay-logged-in: supabase-js rehydrates from storage + silently refreshes), then tracks changes
 * via onAuthStateChange. The supabase client is INJECTED so this is testable with a fake.
 */
export function AuthProvider({ client, children }: { client: SupabaseClient; children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>("loading");
  const [user, setUser] = useState<AuthUser | null>(null);

  useEffect(() => {
    let active = true;

    function applySession(session: Session | null): void {
      if (!active) return;
      if (session && session.user) {
        setUser({
          id: session.user.id,
          email: session.user.email ?? null,
          user_metadata: normalizeUserMetadata(session.user.user_metadata),
        });
        setStatus("authenticated");
      } else {
        setUser(null);
        setStatus("unauthenticated");
      }
    }

    client.auth
      .getSession()
      .then(({ data }) => applySession(data.session))
      .catch(() => applySession(null));

    const { data } = client.auth.onAuthStateChange((_event, session) => applySession(session));

    return () => {
      active = false;
      data.subscription.unsubscribe();
    };
  }, [client]);

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      user,
      signIn: (credentials: Credentials) => signInWithSupabase(client, credentials),
      signOut: () => signOut(client),
    }),
    [client, status, user],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

function normalizeUserMetadata(value: unknown): { readonly full_name?: unknown } | null {
  if (value === null || typeof value !== "object") {
    return null;
  }
  return { full_name: (value as { readonly full_name?: unknown }).full_name };
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (ctx === null) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return ctx;
}
